import { TFunction } from "i18next";
import { ApiErrorResponse, NETWORK_ERROR } from "@stringf/services/api";

export function getErrorMessage(err: unknown, t: TFunction): string {
  if (err instanceof Error && err.message === NETWORK_ERROR) {
    return t("errors.network");
  }
  return err instanceof Error ? err.message : String(err);
}

const AUTH_ERROR_KEYS: Record<number, string> = {
  1027: "errors.auth.noAccount",
  1029: "errors.auth.alreadyRegistered",
  1030: "errors.auth.domainNotAccepted",
  2500: "errors.auth.codeExpired",
  2501: "errors.auth.tooManyAttempts",
  2502: "errors.auth.wrongCode",
  2003: "errors.auth.resetTimedOut",
  2004: "errors.auth.passkeySaveFailed",
  2504: "errors.auth.blocked",
  429: "errors.auth.tooManyRequests",
};

/** "please wait 87 seconds" -> 87. The server words the cooldown; clients only read the number. */
const waitSeconds = (message: string): number | undefined => {
  const m = /(\d+)\s*(?:s\b|sec)/i.exec(message);
  return m ? Number(m[1]) : undefined;
};

/**
 * Localized copy for a failed auth call (signup, passkey reset, password reset),
 * chosen by the backend's business code. The server's own message is English or
 * Mongolian regardless of the user's language, so it is never shown, apart from the
 * cooldown seconds in 2503.
 */
export function getAuthErrorMessage(err: unknown, t: TFunction): string {
  if (err instanceof ApiErrorResponse) {
    if (err.statusCode === 2503) {
      const seconds = waitSeconds(err.message);
      return seconds === undefined
        ? t("errors.auth.waitMinutes")
        : t("errors.auth.waitSeconds", { seconds });
    }
    const key = AUTH_ERROR_KEYS[err.statusCode];
    if (key) return t(key);
  }
  if (err instanceof Error && err.message === NETWORK_ERROR) return t("errors.network");
  return t("errors.generic");
}

/**
 * Copy for a failed Domain request. The two refusals a visitor can act on are 400s
 * told apart only by the server's message (stringr `domains/mod.rs`), so that text is
 * matched, never shown. Every other 400 means the client sent something the form
 * already checked, which is a bug and reads as generic.
 */
export function getDomainRequestErrorMessage(err: unknown, t: TFunction): string {
  if (err instanceof ApiErrorResponse) {
    if (err.statusCode === 400 && /already supported/i.test(err.message)) {
      return t("pulse.request.error.alreadySupported");
    }
    if (err.statusCode === 400 && /already exists/i.test(err.message)) {
      return t("pulse.request.error.alreadyRequested");
    }
    if (err.statusCode === 429) return t("errors.auth.tooManyRequests");
  }
  if (err instanceof Error && err.message === NETWORK_ERROR) return t("errors.network");
  return t("errors.generic");
}

const MESSAGE_ACTION_ERROR_KEYS: Record<number, string> = {
  3027: "errors.messageAction.off",
  3028: "errors.messageAction.notContent",
  3030: "errors.messageAction.notContent",
  3029: "errors.messageAction.notSender",
  3031: "errors.messageAction.alreadyUnsent",
  3032: "errors.messageAction.oneEmoji",
  3000: "errors.messageAction.noWrite",
  404: "errors.messageAction.gone",
};

/**
 * Copy for a failed unsend, react, hide or reactions call, chosen by the backend's
 * business code (the server's own text is never shown).
 */
export function getMessageActionErrorMessage(err: unknown, t: TFunction): string {
  if (err instanceof ApiErrorResponse) {
    const key = MESSAGE_ACTION_ERROR_KEYS[err.statusCode];
    if (key) return t(key);
  }
  if (err instanceof Error && err.message === NETWORK_ERROR) return t("errors.network");
  return t("errors.generic");
}

const FIELD_RULES = new Set(["required", "min_chars", "max_chars", "chars", "leading", "reserved", "email"]);
/** Fields whose rules read differently from the generic copy (the Brane Handle rule). */
const FIELD_COPY = new Set(["username"]);

/**
 * Per-field copy for a validation 400 (`errors` in the body, stringr ADR-0020), keyed by the
 * request's field name. The first error of a field wins; the server's text is never shown.
 * Empty for anything that is not a validation 400, so callers fall back to their own copy.
 */
export function getFieldErrors(err: unknown, t: TFunction): Record<string, string> {
  const out: Record<string, string> = {};
  if (!(err instanceof ApiErrorResponse)) return out;
  for (const { field, rule, min, max } of err.fieldErrors ?? []) {
    if (field in out) continue;
    if (!FIELD_RULES.has(rule)) out[field] = t("errors.generic");
    else {
      const key = FIELD_COPY.has(field) ? `errors.field.${field}.${rule}` : `errors.field.${rule}`;
      const opts: Record<string, number> = {};
      if (min != null) opts.min = min;
      if (max != null) opts.max = max;
      out[field] = Object.keys(opts).length ? t(key, opts) : t(key);
    }
  }
  return out;
}
